import React, { useState, useEffect } from 'react';
import { claimAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { DollarSign, Check, X, Clock, CheckCircle2, Eye, User, Building, FileText, AlertCircle, Edit3, Send, MessageSquare } from 'lucide-react';

const AdminClaims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [claimToReject, setClaimToReject] = useState(null);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [claimToRevise, setClaimToRevise] = useState(null);

  useEffect(() => {
    loadClaims();
  }, []);

  const loadClaims = async () => {
    try {
      const res = await claimAPI.getClaims();
      setClaims(res.data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (claimId) => {
    try {
      await claimAPI.approve(claimId);
      toast.success('Claim approved and paid to wallet');
      loadClaims();
      setDetailsOpen(false);
    } catch (error) {
      toast.error('Failed to approve claim');
    }
  };

  const handleReject = async (claimId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await claimAPI.reject(claimId, rejectReason);
      toast.success('Claim rejected');
      loadClaims();
      setRejectDialogOpen(false);
      setRejectReason('');
      setClaimToReject(null);
      setDetailsOpen(false);
    } catch (error) {
      toast.error('Failed to reject claim');
    }
  };

  const openRejectDialog = (claim) => {
    setClaimToReject(claim);
    setRejectDialogOpen(true);
  };

  const openRevisionDialog = (claim) => {
    setClaimToRevise(claim);
    setRevisionDialogOpen(true);
  };

  const handleRequestRevision = async () => {
    if (!revisionComment.trim()) {
      toast.error('Please provide revision comments for the agent');
      return;
    }
    try {
      await claimAPI.requestRevision(claimToRevise.id, revisionComment);
      toast.success('Revision request sent to agent');
      loadClaims();
      setRevisionDialogOpen(false);
      setRevisionComment('');
      setClaimToRevise(null);
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    }
  };

  const viewClaimDetails = (claim) => {
    setSelectedClaim(claim);
    setDetailsOpen(true);
  };

  const filteredClaims = claims.filter((c) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return c.status === 'pending';
    if (activeTab === 'revision') return c.status === 'revision_requested';
    return c.status === activeTab;
  });

  const totalPending = claims.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
  const revisionCount = claims.filter(c => c.status === 'revision_requested').length;

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700 border-green-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      revision_requested: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    const labels = {
      paid: 'Paid',
      pending: 'Pending',
      rejected: 'Rejected',
      revision_requested: 'Needs Revision',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {labels[status] || status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-claims">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-[#4E50FF]" />
          Claims Management
        </h1>
        <p className="text-slate-500 mt-1">Review and process commission claims</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600 font-mono">{formatCurrency(totalPending)}</p>
            <p className="text-sm text-amber-700">Pending Claims</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <p className="text-3xl font-bold text-slate-900">{claims.filter(c => c.status === 'pending').length}</p>
            </div>
            <p className="text-sm text-slate-500">Pending Count</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <p className="text-3xl font-bold text-green-600">{claims.filter(c => c.status === 'paid').length}</p>
            </div>
            <p className="text-sm text-slate-500">Paid Claims</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-900">{claims.length}</p>
            <p className="text-sm text-slate-500">Total Claims</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="revision" className={revisionCount > 0 ? 'text-orange-600' : ''}>
            Revision {revisionCount > 0 && `(${revisionCount})`}
          </TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredClaims.length > 0 ? (
            <Card className="border shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600">Claim Type</TableHead>
                    <TableHead className="text-slate-600">Agent</TableHead>
                    <TableHead className="text-slate-600">Amount</TableHead>
                    <TableHead className="text-slate-600">Status</TableHead>
                    <TableHead className="text-slate-600">Date</TableHead>
                    <TableHead className="text-slate-600">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClaims.map((claim) => (
                    <TableRow key={claim.id} data-testid={`claim-row-${claim.id}`}>
                      <TableCell className="text-slate-900 font-medium capitalize">
                        {claim.claim_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{claim.agent_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{claim.booking_id ? `Booking: ${claim.booking_id.slice(0,8)}...` : ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-green-600 font-mono font-semibold">
                        {formatCurrency(claim.amount)}
                      </TableCell>
                      <TableCell>{getStatusBadge(claim.status)}</TableCell>
                      <TableCell className="text-slate-500">{formatDate(claim.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewClaimDetails(claim)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`view-claim-${claim.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {claim.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(claim.id)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                                data-testid={`approve-claim-${claim.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRevisionDialog(claim)}
                                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                                data-testid={`revise-claim-${claim.id}`}
                              >
                                <Edit3 className="w-4 h-4 mr-1" />
                                Revise
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRejectDialog(claim)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`reject-claim-${claim.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {claim.status === 'revision_requested' && (
                            <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                              <Clock className="w-3 h-3 mr-1" />
                              Awaiting Agent
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card className="text-center py-12 border shadow-sm">
              <CardContent>
                <div className="w-16 h-16 bg-[#4E50FF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-[#4E50FF]" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Claims</h3>
                <p className="text-slate-500">
                  {activeTab === 'all' ? 'No claims submitted yet' : `No ${activeTab} claims`}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Claim Details Sheet */}
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedClaim && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#4E50FF]" />
                  Claim Details
                </SheetTitle>
                <SheetDescription>
                  {selectedClaim.claim_type?.replace(/_/g, ' ')} claim submitted on {formatDate(selectedClaim.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  {getStatusBadge(selectedClaim.status)}
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(selectedClaim.amount)}</span>
                </div>

                <Separator />

                {/* Agent/Claimant Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" /> Claimant Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Agent Name</p>
                      <p className="font-medium">{selectedClaim.agent_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Agent Code</p>
                      <p className="font-medium">{selectedClaim.agent_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">User ID</p>
                      <p className="font-medium font-mono text-xs">{selectedClaim.user_id?.slice(0, 12)}...</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Claim Type</p>
                      <p className="font-medium capitalize">{selectedClaim.claim_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Booking/Transaction Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building className="h-4 w-4" /> Related Booking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Booking ID</p>
                      <p className="font-medium font-mono text-xs">{selectedClaim.booking_id || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Property</p>
                      <p className="font-medium">{selectedClaim.property_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Commission Rate</p>
                      <p className="font-medium">{selectedClaim.commission_rate ? `${selectedClaim.commission_rate}%` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Unit Price</p>
                      <p className="font-medium">{selectedClaim.unit_price ? formatCurrency(selectedClaim.unit_price) : 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Commission Breakdown */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <DollarSign className="h-4 w-4" /> Commission Amount
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{formatCurrency(selectedClaim.amount)}</p>
                      <p className="text-sm text-blue-700 mt-1">Total Claim Amount</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Timestamps */}
                <div className="text-sm text-gray-500 space-y-1">
                  <p><strong>Created:</strong> {formatDate(selectedClaim.created_at)}</p>
                  {selectedClaim.updated_at && (
                    <p><strong>Last Updated:</strong> {formatDate(selectedClaim.updated_at)}</p>
                  )}
                  {selectedClaim.paid_at && (
                    <p><strong>Paid:</strong> {formatDate(selectedClaim.paid_at)}</p>
                  )}
                </div>

                <Separator />

                {/* Action Buttons */}
                {selectedClaim.status === 'pending' && (
                  <div className="flex flex-col gap-3 pt-4">
                    <Button
                      className="w-full bg-green-500 hover:bg-green-600"
                      onClick={() => handleApprove(selectedClaim.id)}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve & Pay
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                        onClick={() => openRevisionDialog(selectedClaim)}
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Request Revision
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => openRejectDialog(selectedClaim)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}

                {selectedClaim.status === 'revision_requested' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg text-orange-700">
                      <Edit3 className="h-5 w-5" />
                      <div>
                        <span className="font-medium">Revision Requested</span>
                        <p className="text-sm mt-1">Waiting for agent to update and resubmit</p>
                      </div>
                    </div>
                    {selectedClaim.revision_comment && (
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <p className="text-sm font-medium text-gray-700 mb-1">Revision Comments:</p>
                        <p className="text-sm text-gray-600">{selectedClaim.revision_comment}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedClaim.status === 'paid' && (
                  <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">This claim has been approved and paid</span>
                  </div>
                )}

                {selectedClaim.status === 'rejected' && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <div>
                      <span className="font-medium">This claim was rejected</span>
                      {selectedClaim.reject_reason && (
                        <p className="text-sm mt-1">Reason: {selectedClaim.reject_reason}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Reject Claim
            </DialogTitle>
            <DialogDescription>
              This will permanently reject the claim. Use "Request Revision" instead if the agent can fix the issues.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rejectReason">Rejection Reason</Label>
            <Textarea
              id="rejectReason"
              placeholder="Enter the reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={() => handleReject(claimToReject?.id)}
            >
              <X className="w-4 h-4 mr-1" />
              Reject Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Revision Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Edit3 className="h-5 w-5" />
              Request Revision
            </DialogTitle>
            <DialogDescription>
              The agent will be notified and can update their claim based on your feedback.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
              <MessageSquare className="h-4 w-4 inline mr-2" />
              Be specific about what needs to be corrected so the agent can fix it quickly.
            </div>
            <div>
              <Label htmlFor="revisionComment">What needs to be revised?</Label>
              <Textarea
                id="revisionComment"
                placeholder="e.g., Commission amount doesn't match the booking price. Please verify the calculation and resubmit."
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                className="mt-2"
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleRequestRevision}
            >
              <Send className="w-4 h-4 mr-1" />
              Send Revision Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClaims;
